import React, { useCallback } from "react";

import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import LiveChatIcon from "public/icons/live-chat.svg";
import MailBoxIcon from "public/icons/opened-mail-icon.svg";

import { CardActionArea } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { LadyNameVerified } from "@/components/ui/lady-name-verified/LadyNameVerified";
import { useLadyIdContext } from "@/context/LadyId.context";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { TruncatedWithTooltip } from "@/lib/truncate/TruncatedWithTooltip";
import { LadyMinV2DTO } from "@/models/lady-dtos";
import { clsxm } from "@/utils/tailwindMerge";

export type LadySliderModalBottomButtonsPropTypes = {
  linkToLadyProfile: string;
  from: string;
  firstName: string;
  avatarSrc: string;
  isOnline: boolean;
  ladyDto?: LadyMinV2DTO;
};

export const LadySliderModalBottomButtons: React.FC<
  LadySliderModalBottomButtonsPropTypes
> = ({ avatarSrc, firstName, from, linkToLadyProfile, isOnline, ladyDto }) => {
  const { openModal, removeOne } = useOpenModal();
  const { openModal: MailBoxOpenModal } = useOpenModal({
    disableBackGroundClick: true,
  });
  const { setLadyId } = useLadyIdContext();

  const openModalWithHeader = useCallback(
    (header: string) => () => {
      if (!ladyDto) return;

      removeOne();
      setLadyId(ladyDto.id.toString());

      openModal("PROFILE_ACTION_BUTTONS_MODAL", {
        onClose: removeOne,
        header,
        ladyDtoParam: ladyDto,
      });
    },
    [ladyDto, openModal, removeOne, setLadyId],
  );
  const authContext = useAuthContext();
  const onMailMeClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      if (!ladyDto?.id) return;

      if (authContext.isAuthenticated) {
        return MailBoxOpenModal("CREATE_LETTER_MODAL", {
          ladyId: ladyDto.id,
          ladyDto: ladyDto,
        });
      } else {
        return openModalWithHeader("Mailing Me")();
      }
    },
    [
      MailBoxOpenModal,
      authContext.isAuthenticated,
      ladyDto,
      openModalWithHeader,
    ],
  );

  const renderButton = useCallback(() => {
    if (isOnline) {
      return (
        <Button
          className="text-xs"
          onHoverVariant="gradient-flip"
          startIcon={<LiveChatIcon />}
          onClick={openModalWithHeader("Chatting with Me")}
        >
          live chat
        </Button>
      );
    }

    return (
      <Button
        className="text-xs"
        variant="secondary"
        onHoverVariant="color-reverse"
        iconColor="green"
        onClick={onMailMeClick}
        startIcon={<MailBoxIcon />}
      >
        mailbox
      </Button>
    );
  }, [isOnline, onMailMeClick, openModalWithHeader]);

  return (
    <>
      <div
        className={clsx(
          "mx-auto w-full max-w-full px-5 h-[46px]",
          "flex justify-center",
        )}
      >
        <div className="flex gap-2 items-center justify-between min-w-0 w-full basis-[350px]">
          <div className="flex gap-1 min-w-0 bg-opacity-60 items-center">
            <a href={linkToLadyProfile}>
              <CardActionArea
                className={clsxm("w-8 h-8 rounded-full overflow-clip")}
              >
                <Image
                  alt="Lady avatar"
                  src={avatarSrc}
                  fill
                  unoptimized
                  className="object-cover object-top"
                />
              </CardActionArea>
            </a>
            <div className="flex flex-col justify-start min-w-0">
              <Link href={linkToLadyProfile}>
                <LadyNameVerified
                  className="text-sm font-bold text-white"
                  iconClassName="mb-1"
                  iconWidth={18}
                  iconHeight={18}
                >
                  {firstName}
                </LadyNameVerified>
              </Link>
              <div className="min-w-0 h-[16px] flex justify-start">
                <TruncatedWithTooltip
                  component={"span"}
                  className={clsxm(
                    "text-xs text-white min-w-0 inline-block flex-nowrap max-w-full",
                  )}
                  style={{ verticalAlign: "super" }}
                >
                  {from}
                </TruncatedWithTooltip>
              </div>
            </div>
          </div>
          <div className="shrink-0">{renderButton()}</div>
        </div>
      </div>
    </>
  );
};
